import { getLinkMetadataApi } from '@/api-calls/link-metadata/get'
import {
  Command,
  createSuggestionItems,
  renderItems,
} from '@/components/tiptap-editor/extensions'
import {
  Bookmark,
  Camera,
  Code,
  GalleryHorizontal,
  Heading2,
  Heading3,
  ImageIcon,
  List,
  ListOrdered,
  Table,
  Text,
  TextQuote,
  VideoIcon,
  X,
} from 'lucide-react'
import { toast } from 'sonner'

function isValidUrl(url: string) {
  try {
    new URL(url)
    return true
  } catch (_e) {
    return false
  }
}

function getUrlFromString(str: string) {
  if (isValidUrl(str)) return str
  try {
    if (str.includes('.') && !str.includes(' ')) {
      return new URL(`https://${str}`).toString()
    }
  } catch (_e) {
    return null
  }
  return null
}

export const suggestionItems = createSuggestionItems([
  // {
  //   title: 'Send Feedback',
  //   description: 'Let us know how we can improve.',
  //   icon: <MessageSquarePlus size={18} />,
  //   command: ({ editor, range }) => {
  //     editor.chain().focus().deleteRange(range).run()
  //     window.open('/feedback', '_blank')
  //   },
  // },
  {
    title: 'Text',
    description: 'Just start typing with plain text.',
    searchTerms: ['p', 'paragraph'],
    icon: <Text size={18} />,
    command: ({ editor, range }) => {
      editor
        .chain()
        .focus()
        .deleteRange(range)
        .toggleNode('paragraph', 'paragraph')
        .run()
    },
  },
  {
    title: 'Heading',
    description: 'Medium section heading.',
    searchTerms: ['subtitle', 'medium'],
    icon: <Heading2 size={18} />,
    command: ({ editor, range }) => {
      editor
        .chain()
        .focus()
        .deleteRange(range)
        .setNode('heading', { level: 2 })
        .run()
    },
  },
  {
    title: 'Subheading',
    description: 'Small section heading.',
    searchTerms: ['subtitle', 'small'],
    icon: <Heading3 size={18} />,
    command: ({ editor, range }) => {
      editor
        .chain()
        .focus()
        .deleteRange(range)
        .setNode('heading', { level: 3 })
        .run()
    },
  },
  {
    title: 'Bullet List',
    description: 'Create a simple bullet list.',
    searchTerms: ['unordered', 'point'],
    icon: <List size={18} />,
    command: ({ editor, range }) => {
      editor.chain().focus().deleteRange(range).toggleBulletList().run()
    },
  },
  {
    title: 'Numbered List',
    description: 'Create a list with numbering.',
    searchTerms: ['ordered'],
    icon: <ListOrdered size={18} />,
    command: ({ editor, range }) => {
      editor.chain().focus().deleteRange(range).toggleOrderedList().run()
    },
  },
  {
    title: 'Quote',
    description: 'Capture a quote.',
    searchTerms: ['blockquote'],
    icon: <TextQuote size={18} />,
    command: ({ editor, range }) =>
      editor
        .chain()
        .focus()
        .deleteRange(range)
        .toggleNode('paragraph', 'paragraph')
        .toggleBlockquote()
        .run(),
  },
  {
    title: 'Code',
    description: 'Capture a code snippet.',
    searchTerms: ['codeblock'],
    icon: <Code size={18} />,
    command: ({ editor, range }) =>
      editor.chain().focus().deleteRange(range).toggleCodeBlock().run(),
  },
  {
    title: 'Table',
    description: 'Insert a table with rows and columns.',
    searchTerms: ['table', 'grid', 'rows', 'columns', 'spreadsheet'],
    icon: <Table size={18} />,
    command: ({ editor, range }) =>
      editor
        .chain()
        .focus()
        .deleteRange(range)
        .insertTable({ rows: 3, cols: 3, withHeaderRow: true })
        .run(),
  },
  {
    title: 'Image',
    description: 'Upload an image from your computer.',
    searchTerms: ['photo', 'picture', 'media'],
    icon: <ImageIcon size={18} />,
    command: ({ editor, range }) => {
      // Open a file picker for an image
      const input = document.createElement('input')
      input.type = 'file'
      input.accept = 'image/*'
      input.onchange = async () => {
        if (!input.files?.length) return
        const file = input.files[0]

        // Delete the slash command range first
        editor.chain().focus().deleteRange(range).run()

        // Use the uploadImage command which handles validation, placeholder, and API upload
        editor.commands.uploadImage(file)
      }
      input.click()
    },
  },
  {
    title: 'Unsplash',
    description: 'Search and insert a photo from Unsplash.',
    searchTerms: ['unsplash', 'photo', 'stock', 'image', 'search'],
    icon: <Camera size={18} />,
    command: ({ editor, range }) => {
      editor.chain().focus().deleteRange(range).run()
      // The picker dialog lives in the editor page; suggestion items are
      // static, so hand the editor instance over via an event.
      window.dispatchEvent(
        new CustomEvent('editor:open-unsplash', { detail: { editor } }),
      )
    },
  },
  {
    title: 'Gallery',
    description: 'Upload multiple images in a grid layout.',
    searchTerms: ['gallery', 'images', 'photos', 'grid', 'multiple'],
    icon: <GalleryHorizontal size={18} />,
    command: ({ editor, range }) => {
      editor.chain().focus().deleteRange(range).insertGallery().run()
    },
  },
  {
    title: 'Youtube',
    description: 'Embed a Youtube video.',
    searchTerms: ['video', 'youtube', 'embed'],
    icon: <VideoIcon size={18} />,
    command: ({ editor, range }) => {
      const videoLink = prompt('Please enter Youtube Video Link') as string
      //From https://regexr.com/3dj5t
      const ytregex = new RegExp(
        /^((?:https?:)?\/\/)?((?:www|m)\.)?((?:youtube\.com|youtu.be))(\/(?:[\w\-]+\?v=|embed\/|v\/)?)([\w\-]+)(\S+)?$/,
      )

      if (ytregex.test(videoLink)) {
        editor
          .chain()
          .focus()
          .deleteRange(range)
          .setYoutubeVideo({
            src: videoLink,
          })
          .run()
      } else {
        if (videoLink !== null) {
          alert('Please enter a correct Youtube Video Link')
        }
      }
    },
  },
  {
    title: 'X',
    description: 'Embed a X post.',
    searchTerms: ['twitter', 'x', 'embed'],
    icon: <X size={18} />,
    command: ({ editor, range }) => {
      const tweetLink = prompt('Please enter X post link') as string
      const tweetRegex = new RegExp(
        /^https?:\/\/(www\.)?x\.com\/([a-zA-Z0-9_]{1,15})(\/status\/(\d+))?(\/\S*)?$/,
      )

      if (tweetRegex.test(tweetLink)) {
        editor
          .chain()
          .focus()
          .deleteRange(range)
          .setTweet({
            src: tweetLink,
          })
          .run()
      } else {
        if (tweetLink !== null) {
          alert('Please enter a correct Twitter Link')
        }
      }
    },
  },
  {
    title: 'Link Card',
    description: 'Embed a link with rich preview.',
    searchTerms: ['link', 'card', 'preview', 'bookmark', 'url', 'embed'],
    icon: <Bookmark size={18} />,
    command: async ({ editor, range }) => {
      const urlInput = prompt('Please enter a URL') as string

      if (!urlInput) {
        return
      }

      const url = getUrlFromString(urlInput)

      if (!url) {
        toast.error('Please enter a valid URL')
        return
      }

      // Check if it's a Twitter or YouTube URL (let those extensions handle it)
      const isTwitterUrl = /(https?:\/\/)?(www\.)?x\.com\//.test(url)
      const isYoutubeUrl =
        /(https?:\/\/)?(www\.)?(youtube\.com|youtu\.be)\//.test(url)

      if (isTwitterUrl || isYoutubeUrl) {
        toast.error('Use Twitter or YouTube commands for these links')
        return
      }

      editor.chain().focus().deleteRange(range).run()

      // Show loading toast
      const loadingToast = toast.loading('Fetching link metadata...')

      try {
        const metadata = await getLinkMetadataApi.queryFn({ url })

        if (metadata) {
          editor
            .chain()
            .focus()
            .insertLinkCard({
              url: metadata.url,
              title: metadata.title,
              description: metadata.description,
              image: metadata.image,
            })
            .run()

          toast.dismiss(loadingToast)
          toast.success('Link card added successfully')
        }
      } catch (error) {
        console.error('Error fetching link metadata:', error)
        toast.dismiss(loadingToast)
        toast.error('Failed to fetch link metadata. Please try again.')
      }
    },
  },
])

export const slashCommand = Command.configure({
  suggestion: {
    items: () => suggestionItems,
    render: renderItems,
  },
})
